import React from 'react';
import { useParams } from 'react-router-dom';
import { Play, Heart, Shuffle } from 'lucide-react';
import SongMenu from '../SongMenu';

const VibePage = ({ playSong, songs, vibes, likedSongs, toggleLike, user }) => {
  const { vibeId } = useParams();
  const vibe = vibes.find(v => v.id === vibeId);
  
  // Filter songs and randomize order for variety
  const vibeSongs = React.useMemo(() => {
    const filtered = songs.filter(song => song.vibes.includes(vibeId));
    return filtered.sort(() => Math.random() - 0.5);
  }, [songs, vibeId]);

  if (!vibe) {
    return (
      <div className="page-content">
        <div className="error-message">
          <h2>Vibe not found</h2>
          <p>The vibe you're looking for doesn't exist.</p>
        </div>
      </div>
    );
  }

  const handlePlayAll = () => {
    if (vibeSongs.length > 0) {
      playSong(vibeSongs[0], vibeSongs);
    }
  };

  const handleShuffle = () => {
    if (vibeSongs.length > 0) {
      const shuffled = [...vibeSongs].sort(() => Math.random() - 0.5);
      playSong(shuffled[0], shuffled);
    }
  };

  return (
    <div className="page-content">
      <div className="vibe-header">
        <div 
          className="vibe-header-background"
          style={{ 
            background: `linear-gradient(135deg, ${vibe.color}40, ${vibe.color}20)`,
          }}
        >
          <div className="vibe-header-content">
            <div className="vibe-header-info">
              <span className="vibe-type">VIBE</span>
              <h1 className="vibe-title">{vibe.name}</h1>
              <p className="vibe-description">{vibe.description}</p>
              <div className="vibe-stats">
                <span>{vibeSongs.length} songs</span>
                <span>•</span>
                <span>Created by MetaSoul</span>
              </div>
            </div>
            <div 
              className="vibe-header-artwork"
              style={{ backgroundColor: vibe.color }}
            >
              {vibe.image_url ? (
                <img 
                  src={vibe.image_url} 
                  alt={vibe.name}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    borderRadius: '8px'
                  }}
                />
              ) : (
                <div className="vibe-artwork-content">
                  <span className="vibe-artwork-text">{vibe.name}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="vibe-controls">
        <button className="play-all-button" onClick={handlePlayAll}>
          <Play size={20} fill="white" />
          <span>Play All</span>
        </button>
        <button className="shuffle-button" onClick={handleShuffle}>
          <Shuffle size={20} />
          <span>Shuffle</span>
        </button>
      </div>

      <div className="vibe-songs">
        <div className="songs-header">
          <div className="songs-header-index">#</div>
          <div className="songs-header-title">Title</div>
          <div className="songs-header-album">Album</div>
          <div className="songs-header-duration">Duration</div>
          <div className="songs-header-actions"></div>
        </div>

        <div className="songs-list">
          {vibeSongs.map((song, index) => (
            <div key={song.id} className="song-row">
              <div className="song-row-index">{index + 1}</div>
              <div className="song-row-image">
                <img src={song.albumArt} alt={song.title} />
                <button 
                  className="row-play-button"
                  onClick={() => playSong(song, vibeSongs)}
                >
                  <Play size={16} />
                </button>
              </div>
              <div className="song-row-info">
                <div className="song-row-title">{song.title}</div>
                <div className="song-row-artist">{song.artist}</div>
              </div>
              <div className="song-row-album">{song.album}</div>
              <div className="song-row-duration">
                {Math.floor(song.duration / 60)}:{(song.duration % 60).toString().padStart(2, '0')}
              </div>
              <div className="song-row-actions">
                <button 
                  className={`song-row-like ${likedSongs.has(song.id) ? 'liked' : ''}`}
                  onClick={() => toggleLike(song.id)}
                >
                  <Heart size={16} fill={likedSongs.has(song.id) ? '#9D4EDD' : 'none'} />
                </button>
                <SongMenu song={song} user={user} />
              </div>
            </div>
          ))}
        </div>

        {vibeSongs.length === 0 && (
          <div className="empty-vibe">
            <h3>No songs in this vibe yet</h3>
            <p>Check back later for new additions to {vibe.name}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default VibePage;